import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { IdiomaService } from 'src/app/services/idioma.service';
import { ColumnaOrden, Orden, TipoCampo } from '../models/columna-orden';

export interface CampoBusqueda extends ColumnaOrden {
  control: string;
  tipo: TipoCampo;
}

const FORMATO_ISO = 'YYYY-MM-DD';

@Component({
  selector: 'app-busqueda-avanzada',
  templateUrl: './busqueda-avanzada.component.html',
  styleUrls: ['./busqueda-avanzada.component.css']
})
export class BusquedaAvanzadaComponent implements OnInit, OnDestroy {

  @Input() columnas: ColumnaOrden[] = [];
  @Input() filtro: FormGroup;

  @Input() campo: string;
  @Input() direccion: string = 'asc';

  @Output() buscar = new EventEmitter<{ filtros: any, orden: Orden }>();

  public t: any;
  public modalRef: BsModalRef;
  public campos: CampoBusqueda[] = [];
  public valores: any = {};
  public campoOrden: string;
  public direccionOrden: string;

  private subscripcionTextos: Subscription;

  constructor(
    private _idioma: IdiomaService,
    private _modalSvc: BsModalService
  ) { }

  ngOnInit(): void {
    this.subscripcionTextos = this._idioma.getTextos$().subscribe(texto => {
      this.t = texto;
    });
  }

  ngOnDestroy(): void {
    if (this.subscripcionTextos) {
      this.subscripcionTextos.unsubscribe();
    }
  }

  get disponible(): boolean {
    return !!this.filtro && !!this.columnas && this.columnas.length > 0;
  }

  abrir(plantilla: TemplateRef<any>): void {
    this.campos = this.relevarCampos();
    this.valores = {};

    this.campos.forEach(campo => {
      const guardado = this.filtro.value[campo.control];
      const texto = guardado === null || guardado === undefined ? '' : String(guardado);

      // El tipo puede terminar siendo otro que el declarado; ver aControl().
      const control = this.aControl(campo.tipo, texto);

      campo.tipo = control.tipo;
      this.valores[campo.control] = control.valor;
    });

    this.campoOrden = this.campo;
    this.direccionOrden = this.direccion || 'asc';

    this.modalRef = this._modalSvc.show(plantilla, { class: 'modal-lg' });
  }

  private relevarCampos(): CampoBusqueda[] {
    return this.columnas
      .map(columna => ({
        campo: columna.campo,
        etiqueta: columna.etiqueta,
        tipo: columna.tipo || 'texto',
        control: columna.campo.split('.').join('$')
      }))
      .filter(columna => !!this.filtro.controls[columna.control]);
  }

  private get formatoFecha(): string {
    return (this._idioma.getTexto('formatofecha') || FORMATO_ISO).toUpperCase();
  }

  private aControl(tipo: TipoCampo, texto: string): { tipo: TipoCampo, valor: string } {
    if (!texto) {
      return { tipo, valor: '' };
    }

    if (tipo === 'fecha') {
      const fecha = moment(texto, this.formatoFecha, true);

      return fecha.isValid()
        ? { tipo, valor: fecha.format(FORMATO_ISO) }
        : { tipo: 'texto', valor: texto };
    }

    if (tipo === 'numero') {
      return isNaN(Number(texto))
        ? { tipo: 'texto', valor: texto }
        : { tipo, valor: texto };
    }

    if (tipo === 'booleano') {
      const opcion = this.opcionBooleana(texto);

      return opcion
        ? { tipo, valor: opcion }
        : { tipo: 'texto', valor: texto };
    }

    return { tipo, valor: texto };
  }

  private aFiltro(campo: CampoBusqueda, valor: any): string {
    if (valor === null || valor === undefined || valor === '') {
      return '';
    }

    if (campo.tipo === 'fecha') {
      const fecha = moment(String(valor), FORMATO_ISO, true);

      return fecha.isValid() ? fecha.format(this.formatoFecha) : '';
    }

    return String(valor);
  }

  private opcionBooleana(texto: string): string {
    const escrito = texto.trim().toLowerCase();

    if (escrito === String(this.t.si || '').toLowerCase()) { return this.t.si; }
    if (escrito === String(this.t.no || '').toLowerCase()) { return this.t.no; }

    return '';
  }

  get hayAlgoCargado(): boolean {
    return Object.keys(this.valores).some(clave => {
      const valor = this.valores[clave];

      return valor !== '' && valor !== null && valor !== undefined;
    });
  }

  onLimpiar(): void {
    this.campos = this.relevarCampos();
    this.valores = {};

    this.campos.forEach(campo => { this.valores[campo.control] = ''; });
  }

  soltarFoco(evento: Event): void {
    const elemento = evento.target as HTMLElement;

    if (elemento && typeof elemento.blur === 'function') {
      elemento.blur();
    }
  }

  onBuscar(): void {
    const filtros = {};

    this.campos.forEach(campo => {
      filtros[campo.control] = this.aFiltro(campo, this.valores[campo.control]);
    });

    this.buscar.emit({
      filtros,
      orden: { campo: this.campoOrden, direccion: this.direccionOrden || 'asc' }
    });

    this.cerrar();
  }

  cerrar(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
  }
}
